/*
 * Corkopia — scroll depth for the legacy product card.
 *
 * This file used to carry six effects: scroll reveals, a cursor spotlight,
 * magnetic buttons, a hero parallax, a document scroll signal, and card depth.
 * Five of them wrote CSS custom properties and data attributes that no
 * stylesheet in the theme reads — `--spot-x`, `--hero-x`, `--archive-scroll`,
 * `[data-cork-spotlight]`, `[data-cork-magnet]`, `.is-revealed` — and their
 * selectors (`.hero__content`, `.arrival-grid`, `.process__grid`) belong to a
 * layer the storefront no longer renders. They cost four document-level
 * pointermove handlers and a body-wide MutationObserver to produce nothing.
 *
 * Card depth is the one that still lands: `--c-card-depth` is consumed by
 * `.cork-product-card__image` in corkopia-product-experience.css, and
 * `cork-product-card` is still rendered on search and the prebuilt rails. The
 * entrance motion for the current catalogue layer lives in corkopia-plate.css
 * as a scroll-driven timeline, which needs no script at all.
 */
(() => {
  'use strict';

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reducedMotion || !('IntersectionObserver' in window)) return;

  const depthCards = new Set();
  const preparedDepthCards = new WeakSet();

  const depthObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) depthCards.add(entry.target);
      else depthCards.delete(entry.target);
    });
  }, { rootMargin: '12% 0px' });

  function prepareProductDepth(root = document) {
    const cards = [
      ...(root.matches?.('[data-cork-product-card]') ? [root] : []),
      ...(root.querySelectorAll?.('[data-cork-product-card]') || [])
    ];
    cards.forEach((card) => {
      if (preparedDepthCards.has(card)) return;
      preparedDepthCards.add(card);
      depthObserver.observe(card);
    });
  }

  let scrollFrame = 0;
  function updateDepth() {
    if (scrollFrame) return;
    scrollFrame = window.requestAnimationFrame(() => {
      depthCards.forEach((card) => {
        const bounds = card.getBoundingClientRect();
        const center = bounds.top + bounds.height / 2;
        const normalized = Math.max(-1, Math.min(1, (center - window.innerHeight / 2) / window.innerHeight));
        card.style.setProperty('--c-card-depth', `${Number((-normalized * 5).toFixed(2))}px`);
      });
      scrollFrame = 0;
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    prepareProductDepth();
    updateDepth();

    // Scoped to the cards this file still drives, rather than the whole body.
    new MutationObserver((records) => {
      records.forEach((record) => record.addedNodes.forEach((node) => {
        if (node.nodeType === 1) prepareProductDepth(node);
      }));
    }).observe(document.body, { childList: true, subtree: true });
  });

  window.addEventListener('scroll', updateDepth, { passive: true });
})();
